/*@flow*/
import type { Node } from 'react';
import React from 'react';
import * as t from 'lib/t';
import cx from 'classnames';
import Breakpoints from '../util/Breakpoints';
import ErrorComponent from './ErrorComponent';
import Help from './Help';
import FormGroup from './FormGroup';
import Label from './Label';
import type { CheckboxLocals } from '../types';
import VerticalCols from './VerticalCols';

const CheckboxConfig = t.struct(
  {
    horizontal: t.maybe(Breakpoints),
    vertical: t.maybe(Breakpoints),
  },
  'CheckboxConfig'
);

function getConfig(locals: CheckboxLocals) {
  return CheckboxConfig(locals.config || {});
}

function getAttrs(locals: CheckboxLocals) {
  let attrs = {};
  attrs.type = 'checkbox';
  attrs.className = 'form-check-input';
  attrs.style = { marginTop: '1.1rem' };
  attrs = t.mixin(attrs, locals.attrs);

  attrs.disabled = locals.disabled;
  attrs.checked = locals.value;
  attrs.onChange = (evt) => locals.onChange(evt.target.checked);
  if (locals.help) {
    attrs['aria-describedby'] = attrs['aria-describedby'] || attrs.id + '-tip';
  }
  return attrs;
}

type Registry = {
  Checkbox: React$StatelessFunctionalComponent<*>,
  Help: React$StatelessFunctionalComponent<*>,
  Vertical: React$StatelessFunctionalComponent<*>,
  Horizontal: React$StatelessFunctionalComponent<*>,
  FormGroup: React$StatelessFunctionalComponent<*>,
  Label: React$StatelessFunctionalComponent<*>,
};
const clone = (overrides: $Shape<Registry> = {}) => {
  let registry = {};

  function Checkbox(locals: CheckboxLocals) {
    const attrs = getAttrs(locals);
    const className = {
      disabled: attrs.disabled,
      'form-check': true,
    };
    return (
      <div className={cx(className)}>
        <input {...attrs} />
        <registry.Label
          {...{
            label: locals.label,
            htmlFor: attrs.id,
            breakpoints: locals.config.horizontal,
            className: cx('form-check-label ml-2', {
              'text-danger font-weight-bold': locals.hasError,
            }),
          }}
        />
      </div>
    );
  }

  function Vertical(locals: CheckboxLocals) {
    return (
      <VerticalCols {...locals}>
        <registry.Checkbox {...locals} />
        <ErrorComponent {...locals} />
        <registry.Help {...locals} />
      </VerticalCols>
    );
  }

  function Horizontal(locals: CheckboxLocals) {
    const className = locals.config.horizontal.getOffsetClassName();
    return (
      <div className={cx(className)}>
        <registry.Checkbox {...locals} />
        <ErrorComponent {...locals} />
        <registry.Help {...locals} />
      </div>
    );
  }

  function CheckboxComponent(locals: CheckboxLocals) {
    const locals2 = { ...locals };
    locals2.config = getConfig(locals2);

    const children = locals2.config.horizontal ? (
      <registry.Horizontal {...locals2} />
    ) : (
      <registry.Vertical {...locals2} />
    );

    return <registry.FormGroup {...locals2}>{children}</registry.FormGroup>;
  }

  CheckboxComponent.clone = clone;

  registry = {
    Checkbox,
    Help,
    Vertical,
    Horizontal,
    FormGroup,
    Label,
    ...overrides,
  };

  return CheckboxComponent;
};

export default (clone(): (locals: CheckboxLocals) => Node);
